import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface CopilotMessage {
  id:         string;
  role:       'user' | 'assistant';
  text:       string;
  toolsUsed?: string[];
  createdAt:  Date;
  streaming?: boolean; // true enquanto o texto ainda está chegando
}

export interface CopilotContext {
  page?:        string;   // 'contacts' | 'campaigns' | 'events' | 'dashboard'
  entity_id?:   string;
  entity_name?: string;
}

interface GeminiPart    { text: string }
interface GeminiMessage { role: 'user' | 'model'; parts: GeminiPart[] }

// Evento SSE emitido pelo Edge Function
type SseEvent =
  | { t: 'tool';  name: string }
  | { t: 'chunk'; text: string }
  | { t: 'done';  tools_used: string[]; tenant_id: string }
  | { t: 'error'; message: string };

export function useCopilot(context?: CopilotContext) {
  const [messages,        setMessages]        = useState<CopilotMessage[]>([]);
  const [isLoading,       setIsLoading]       = useState(false);
  const [error,           setError]           = useState<string | null>(null);
  const [toolsInProgress, setToolsInProgress] = useState<string[]>([]);

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || isLoading) return;

    const userMsg: CopilotMessage = {
      id:        crypto.randomUUID(),
      role:      'user',
      text:      text.trim(),
      createdAt: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);
    setError(null);
    setToolsInProgress([]);

    // Cria um placeholder de assistente que será preenchido via streaming
    const assistantId = crypto.randomUUID();
    const assistantPlaceholder: CopilotMessage = {
      id:        assistantId,
      role:      'assistant',
      text:      '',
      streaming: true,
      createdAt: new Date(),
    };
    setMessages((prev) => [...prev, assistantPlaceholder]);

    try {
      // Histórico no formato Gemini (exclui o placeholder que acabou de ser inserido)
      const history: GeminiMessage[] = [...messages, userMsg].map((m) => ({
        role:  m.role === 'user' ? 'user' : 'model',
        parts: [{ text: m.text }],
      }));

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Sessão expirada. Faça login novamente.');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
      const res = await fetch(`${supabaseUrl}/functions/v1/copilot`, {
        method:  'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type':  'application/json',
        },
        body: JSON.stringify({ messages: history, context }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(errData.error ?? `Erro ${res.status}`);
      }

      if (!res.body) throw new Error('Resposta sem corpo');

      const contentType = res.headers.get('Content-Type') ?? '';
      let accText      = '';
      let finalTools: string[] = [];

      if (contentType.includes('text/event-stream')) {
        // ── Modo SSE (Edge Function atualizada) ───────────────────────────────
        const reader  = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer    = '';

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const raw = line.slice(6).trim();
            if (!raw) continue;

            let event: SseEvent;
            try {
              event = JSON.parse(raw) as SseEvent;
            } catch {
              continue;
            }

            if (event.t === 'tool') {
              setToolsInProgress((prev) =>
                prev.includes(event.name) ? prev : [...prev, event.name],
              );
            } else if (event.t === 'chunk') {
              accText += event.text;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId ? { ...m, text: accText } : m,
                ),
              );
            } else if (event.t === 'done') {
              finalTools = event.tools_used ?? [];
            } else if (event.t === 'error') {
              throw new Error(event.message);
            }
          }
        }

      } else {
        // ── Modo JSON legado (Edge Function ainda não atualizada) ─────────────
        const bodyText = await new Response(res.body).text();
        let data: { text?: string; tools_used?: string[]; error?: string };
        try {
          data = JSON.parse(bodyText) as typeof data;
        } catch {
          throw new Error('Resposta inválida do servidor');
        }
        if (data.error) throw new Error(data.error);
        accText    = data.text ?? '';
        finalTools = data.tools_used ?? [];

        // Simula chunks para manter a UX consistente
        const CHUNK = 60;
        for (let i = 0; i < accText.length; i += CHUNK) {
          const chunk = accText.slice(i, i + CHUNK);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? { ...m, text: accText.slice(0, i + chunk.length) }
                : m,
            ),
          );
          // Pequeno yield para que o React possa atualizar entre chunks
          await new Promise((r) => setTimeout(r, 16));
        }
      }

      // ── Finaliza o placeholder com o texto completo + ferramentas ────────────
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId
            ? {
                ...m,
                text:      accText || '(sem resposta)',
                toolsUsed: finalTools.length ? finalTools : undefined,
                streaming: false,
              }
            : m,
        ),
      );
      setToolsInProgress([]);

    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Erro desconhecido';
      setError(msg);
      // Remove o placeholder e a mensagem do usuário
      setMessages((prev) => prev.filter((m) => m.id !== assistantId && m.id !== userMsg.id));
      setToolsInProgress([]);
    } finally {
      setIsLoading(false);
    }
  }, [messages, isLoading, context]);

  const clearHistory = useCallback(() => {
    setMessages([]);
    setError(null);
    setToolsInProgress([]);
  }, []);

  return { messages, isLoading, error, toolsInProgress, sendMessage, clearHistory };
}
